// Expression is resolved against written time before form expansion. A passage,
// repeat or D.S. therefore inherits the dynamic at its written destination,
// rather than whichever marking happened to play before the jump.

import { fractionToNumber, measureQuarters } from './duration';
import { voiceTiming } from './timing';
import type { DynamicMark, NoteEvent, Score } from './types';

const EPSILON = 1e-9;

/** Relative linear gain; mf preserves the existing unmarked instrument level. */
export const DYNAMIC_GAIN: Readonly<Record<DynamicMark, number>> = {
  pp: 0.35,
  p: 0.5,
  mp: 0.72,
  mf: 1,
  f: 1.2,
  ff: 1.4,
};

export interface DynamicEnvelopePoint {
  offsetQuarters: number;
  /** Relative to this event's onset dynamicGain, not its accent. */
  gain: number;
  /** Interpolate from the previous point; omitted means change at this point. */
  ramp?: boolean;
}

/** A playback-clock projection, shared without importing the audio layer. */
export interface PlaybackExpressionPoint {
  beatOffset: number;
  gain: number;
  ramp?: boolean;
}

export type ExpressionPoint = PlaybackExpressionPoint;

export interface ScoreEventExpression {
  /** Dynamic alone, used to join tied envelopes without reapplying an accent. */
  dynamicGain: number;
  /** Dynamic × strongest attack accent; omitted when exactly the baseline (1). */
  velocity?: number;
  /** Proportion of written duration to sound; omitted keeps the usual envelope. */
  articulationGate?: number;
  /** Emitted only when dynamics change during this event's written duration. */
  dynamicEnvelope?: DynamicEnvelopePoint[];
}

/** Marks on a tie continuation must not create another attack. */
export function articulationPlayback(
  event: Pick<NoteEvent, 'articulations'>,
): Pick<ScoreEventExpression, 'velocity' | 'articulationGate'> {
  const marks = event.articulations ?? [];
  const velocity = marks.includes('marcato') ? 1.35 : marks.includes('accent') ? 1.2 : undefined;
  const articulationGate = marks.includes('staccato')
    ? 0.5
    : marks.includes('tenuto')
      ? 1
      : marks.includes('marcato')
        ? 0.75
        : undefined;
  return {
    ...(velocity !== undefined ? { velocity } : {}),
    ...(articulationGate !== undefined ? { articulationGate } : {}),
  };
}

interface WrittenEvent {
  event: NoteEvent;
  staffKey: string;
  voiceIndex: number;
  start: number;
  duration: number;
  order: number;
}

interface DynamicChange {
  time: number;
  gain: number;
  voiceIndex: number;
}

interface Hairpin {
  start: number;
  end: number;
  direction: 1 | -1;
  order: number;
}

interface GainSegment {
  start: number;
  end: number;
  startGain: number;
  endGain: number;
}

function writtenEvents(score: Score): WrittenEvent[] {
  let position = 0;
  let meter: [number, number] = [4, 4];
  const starts = score.form.map((measure) => {
    if (measure.timeSig) meter = measure.timeSig;
    const start = position;
    position +=
      measure.pickupBeats === undefined
        ? fractionToNumber(measureQuarters(meter))
        : (measure.pickupBeats * 4) / meter[1];
    return start;
  });
  const result: WrittenEvent[] = [];
  score.parts.forEach((part) => {
    part.measures.forEach((measure, index) => {
      if (index >= starts.length) return;
      measure.voices.forEach((voice, voiceIndex) => {
        voiceTiming(voice).forEach(({ event, start, duration }) => {
          result.push({
            event,
            staffKey: `${part.id}\u0000${event.staff ?? voice.staff ?? 0}`,
            voiceIndex,
            start: starts[index] + fractionToNumber(start),
            duration: fractionToNumber(duration),
            order: result.length,
          });
        });
      });
    });
  });
  return result;
}

function nextDynamicGain(gain: number, direction: 1 | -1): number {
  const levels = Object.values(DYNAMIC_GAIN);
  return direction === 1
    ? (levels.find((level) => level > gain + EPSILON) ?? levels[levels.length - 1])
    : ([...levels].reverse().find((level) => level < gain - EPSILON) ?? levels[0]);
}

function interpolate(segment: GainSegment, time: number): number {
  const proportion = Math.max(
    0,
    Math.min(1, (time - segment.start) / (segment.end - segment.start)),
  );
  return segment.startGain + (segment.endGain - segment.startGain) * proportion;
}

function staffGainSegments(events: WrittenEvent[], hairpins: Hairpin[]): GainSegment[] {
  const sorted = [...events].sort((a, b) => a.start - b.start || a.order - b.order);
  const changes: DynamicChange[] = [];
  for (const { event, start, voiceIndex } of sorted) {
    if (!event.dynamic) continue;
    // Multiple voices share a staff dynamic. The first written voice is the
    // authority for conflicting simultaneous marks. Within that same voice,
    // later marks win: zero-time graces precede their principal, whose own
    // explicit dynamic must not disappear behind a grace-note marking.
    const previous = changes.at(-1);
    const change = { time: start, gain: DYNAMIC_GAIN[event.dynamic], voiceIndex };
    if (previous && Math.abs(previous.time - start) <= EPSILON) {
      if (voiceIndex <= previous.voiceIndex) changes[changes.length - 1] = change;
    } else {
      changes.push(change);
    }
  }
  const end = Math.max(0, ...events.map((event) => event.start + event.duration));
  const boundaries = [
    ...new Set([
      0,
      end,
      ...changes.map((change) => change.time),
      ...hairpins.flatMap((hairpin) => [hairpin.start, hairpin.end]),
    ]),
  ].sort((a, b) => a - b);
  const result: GainSegment[] = [];
  const fallbackTargets = new Map<Hairpin, number>();
  let gain = 1;
  boundaries.forEach((time, index) => {
    const explicit = changes.find((change) => Math.abs(change.time - time) <= EPSILON);
    if (explicit) gain = explicit.gain;
    const nextTime = boundaries[index + 1];
    if (nextTime === undefined || nextTime <= time) return;
    // Duplicate/overlapping imported hairpins never multiply gain. The most
    // recently started span controls the shared staff until that span ends.
    const active = hairpins
      .filter((span) => span.start <= time + EPSILON && span.end > time + EPSILON)
      .sort((a, b) => b.start - a.start || a.order - b.order)[0];
    let endGain = gain;
    if (active) {
      if (!fallbackTargets.has(active) || explicit) {
        fallbackTargets.set(active, nextDynamicGain(gain, active.direction));
      }
      const targetMark = changes.find(
        (change) => change.time > time + EPSILON && change.time <= active.end + EPSILON,
      );
      const targetTime = targetMark?.time ?? active.end;
      const targetGain = targetMark?.gain ?? fallbackTargets.get(active)!;
      endGain = gain + ((targetGain - gain) * (nextTime - time)) / (targetTime - time);
    }
    result.push({ start: time, end: nextTime, startGain: gain, endGain });
    gain = endGain;
  });
  // A final explicit dynamic can occur on a zero-duration grace event.
  const terminal = changes.find((change) => Math.abs(change.time - end) <= EPSILON);
  result.push({
    start: end,
    end: Infinity,
    startGain: terminal?.gain ?? gain,
    endGain: terminal?.gain ?? gain,
  });
  return result;
}

function gainAt(segments: GainSegment[], time: number): number {
  const segment = segments.find((candidate) => candidate.start <= time && candidate.end > time);
  if (!segment) return 1;
  if (segment.startGain === segment.endGain) return segment.startGain;
  return interpolate(segment, time);
}

function eventEnvelope(
  segments: GainSegment[],
  start: number,
  duration: number,
  dynamicGain: number,
): DynamicEnvelopePoint[] | undefined {
  if (duration <= 0) return undefined;
  const end = start + duration;
  const points: DynamicEnvelopePoint[] = [{ offsetQuarters: 0, gain: 1 }];
  let lastGain = dynamicGain;
  for (const segment of segments) {
    if (segment.end <= start || segment.start >= end) continue;
    const from = Math.max(start, segment.start);
    const to = Math.min(end, segment.end);
    const fromGain =
      segment.startGain === segment.endGain ? segment.startGain : interpolate(segment, from);
    if (Math.abs(fromGain - lastGain) > EPSILON) {
      points.push({ offsetQuarters: from - start, gain: fromGain / dynamicGain });
    }
    const toGain =
      segment.startGain === segment.endGain ? segment.endGain : interpolate(segment, to);
    if (Math.abs(toGain - fromGain) > EPSILON) {
      if (points.at(-1)!.offsetQuarters < from - start - EPSILON) {
        points.push({ offsetQuarters: from - start, gain: fromGain / dynamicGain });
      }
      points.push({ offsetQuarters: to - start, gain: toGain / dynamicGain, ramp: true });
    }
    lastGain = toGain;
  }
  if (points.every((point) => Math.abs(point.gain - 1) <= EPSILON)) return undefined;
  if (points.at(-1)!.offsetQuarters < duration - EPSILON) {
    points.push({ offsetQuarters: duration, gain: lastGain / dynamicGain });
  }
  return points;
}

/**
 * Resolve expression for every native event id, including rests and grace notes
 * that carry staff dynamics. Part and effective staff are independent scopes.
 * Hairpins run between anchor onsets; explicit dynamics re-anchor the ramp.
 * With no end mark, a hairpin moves one dynamic level and retains that level.
 */
export function buildScoreExpression(score: Score): ReadonlyMap<string, ScoreEventExpression> {
  const events = writtenEvents(score);
  const byId = new Map(events.map((event) => [event.event.id, event]));
  const staffEvents = new Map<string, WrittenEvent[]>();
  events.forEach((event) => {
    const sameStaff = staffEvents.get(event.staffKey) ?? [];
    sameStaff.push(event);
    staffEvents.set(event.staffKey, sameStaff);
  });
  const staffHairpins = new Map<string, Hairpin[]>();
  score.spanners?.forEach((spanner, order) => {
    if (spanner.type !== 'cresc' && spanner.type !== 'dim') return;
    const from = byId.get(spanner.fromEventId);
    const to = byId.get(spanner.toEventId);
    if (!from || !to || from.staffKey !== to.staffKey || to.start <= from.start + EPSILON) return;
    const hairpins = staffHairpins.get(from.staffKey) ?? [];
    hairpins.push({
      start: from.start,
      end: to.start,
      direction: spanner.type === 'cresc' ? 1 : -1,
      order,
    });
    staffHairpins.set(from.staffKey, hairpins);
  });
  const result = new Map<string, ScoreEventExpression>();
  staffEvents.forEach((sameStaff, staffKey) => {
    const segments = staffGainSegments(sameStaff, staffHairpins.get(staffKey) ?? []);
    sameStaff.forEach(({ event, start, duration }) => {
      const dynamicGain = gainAt(segments, start);
      const articulation = articulationPlayback(event);
      const velocity = dynamicGain * (articulation.velocity ?? 1);
      const dynamicEnvelope = eventEnvelope(segments, start, duration, dynamicGain);
      result.set(event.id, {
        dynamicGain,
        ...(Math.abs(velocity - 1) > EPSILON ? { velocity } : {}),
        ...(articulation.articulationGate !== undefined
          ? { articulationGate: articulation.articulationGate }
          : {}),
        ...(dynamicEnvelope ? { dynamicEnvelope } : {}),
      });
    });
  });
  return result;
}

/** Convert the written quarter-note clock to a caller's playback beat clock. */
export function scaleExpressionEnvelope(
  envelope: readonly DynamicEnvelopePoint[] | undefined,
  beatsPerQuarter: number,
): PlaybackExpressionPoint[] | undefined {
  return envelope?.map(({ offsetQuarters, ...point }) => ({
    ...point,
    beatOffset: offsetQuarters * beatsPerQuarter,
  }));
}

/** Extend one sounding tie; its continuation's accent is deliberately absent. */
export function mergeTiedExpressionEnvelope(
  attack: readonly PlaybackExpressionPoint[] | undefined,
  continuation: readonly PlaybackExpressionPoint[] | undefined,
  offsetBeats: number,
  dynamicRatio: number,
): PlaybackExpressionPoint[] | undefined {
  const result = (attack ?? [{ beatOffset: 0, gain: 1 }]).map((point) => ({ ...point }));
  for (const point of continuation ?? [{ beatOffset: 0, gain: 1 }]) {
    const next = {
      ...point,
      beatOffset: point.beatOffset + offsetBeats,
      gain: point.gain * dynamicRatio,
    };
    const previous = result.at(-1)!;
    if (
      Math.abs(previous.beatOffset - next.beatOffset) <= EPSILON &&
      Math.abs(previous.gain - next.gain) <= EPSILON
    )
      continue;
    result.push(next);
  }
  return result.some((point) => Math.abs(point.gain - 1) > EPSILON) ? result : undefined;
}

function playbackGainAt(envelope: readonly PlaybackExpressionPoint[], time: number): number {
  let previous: PlaybackExpressionPoint = { beatOffset: 0, gain: 1 };
  for (const point of envelope) {
    if (point.beatOffset > time) {
      if (!point.ramp) return previous.gain;
      const proportion = (time - previous.beatOffset) / (point.beatOffset - previous.beatOffset);
      return previous.gain + (point.gain - previous.gain) * Math.max(0, Math.min(1, proportion));
    }
    previous = point;
  }
  return previous.gain;
}

/** Resume/audition a held note at its current level, retaining its remaining ramp. */
export function clipExpressionEnvelope(
  envelope: readonly PlaybackExpressionPoint[] | undefined,
  startBeat: number,
  endBeat = Infinity,
): { gain: number; expression?: PlaybackExpressionPoint[] } {
  if (!envelope?.length) return { gain: 1 };
  const gain = playbackGainAt(envelope, startBeat);
  const points: PlaybackExpressionPoint[] = [{ beatOffset: 0, gain: 1 }];
  for (const point of envelope) {
    if (point.beatOffset <= startBeat) continue;
    if (point.beatOffset > endBeat) {
      if (point.ramp && Number.isFinite(endBeat) && endBeat > startBeat) {
        points.push({
          beatOffset: endBeat - startBeat,
          gain: playbackGainAt(envelope, endBeat) / gain,
          ramp: true,
        });
      }
      break;
    }
    points.push({ ...point, beatOffset: point.beatOffset - startBeat, gain: point.gain / gain });
  }
  return {
    gain,
    ...(points.some((point) => Math.abs(point.gain - 1) > EPSILON) ? { expression: points } : {}),
  };
}
